import { LoanDataUploadForm } from '@pesto/features/src/LoanDataUpload/LoanDataUploadForm';
import { LoansMissingServicingDataPanel } from '@pesto/features/src/LoansMissingServicingData/components/LoansMissingServicingDataPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@pesto/ui/components/Card';
import { Label } from '@pesto/ui/components/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pesto/ui/components/Select';
import { Typography } from '@pesto/ui/components/Typography';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useLoansWithoutServicingDataQuery, useProgramsQuery } from '../../__generated__/graphql/api';
import { routes } from '../../constants/routes';
import { useRole } from '../../hooks/useRole';

export const LoanData = () => {
  const { isPartnerAdmin, role } = useRole();
  const { data, loading } = useProgramsQuery();
  const [pickedProgramId, setPickedProgramId] = useState('');

  const {
    data: gapData,
    loading: gapLoading,
    error: gapError,
    refetch: refetchGaps,
  } = useLoansWithoutServicingDataQuery();

  if (role && !isPartnerAdmin) {
    return <Navigate to={routes.dashboard} replace />;
  }

  const programs = data?.programs ?? [];
  const programId = programs.length === 1 ? programs[0].id : pickedProgramId || null;
  const program = programs.find((candidate) => candidate.id === programId);
  const endpoint = `${import.meta.env.VITE_APP_URI ?? ''}/pcapi/loan_data/csv`;
  const disabled = !loading && programs.length === 0;
  const disabledReason = disabled ? 'No program is associated with your account. Contact Pesto support.' : undefined;

  const programSelector = programs.length > 1 && (
    <div className="flex flex-col gap-2">
      <Label htmlFor="loan-data-program">Program</Label>
      <Select value={pickedProgramId} onValueChange={setPickedProgramId}>
        <SelectTrigger id="loan-data-program" className="max-w-md" data-testid="loan-data-program-select">
          <SelectValue placeholder="Choose a program" />
        </SelectTrigger>
        <SelectContent>
          {programs.map((candidate) => (
            <SelectItem key={candidate.id} value={candidate.id}>
              {candidate.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <main className="flex flex-1 flex-col gap-6 p-4 md:p-8">
      <div className="flex flex-col gap-1">
        <Typography variant="headerMedium">Loan Data</Typography>
        <Typography variant="bodySmall" className="text-muted-foreground">
          Upload a loan servicing CSV, one row per loan keyed by partner account number, to sync servicing data into
          Pesto.
        </Typography>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload CSV</CardTitle>
          <CardDescription>
            One row per loan. Uploads run against{' '}
            <span className="text-foreground font-medium">{program?.name ?? 'the program you choose'}</span>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LoanDataUploadForm
            endpoint={endpoint}
            programId={programId}
            disabled={disabled}
            disabledReason={disabledReason}
            beforeFileField={programSelector}
            onUploadSuccess={() => {
              void refetchGaps();
            }}
          />
        </CardContent>
      </Card>
      <LoansMissingServicingDataPanel
        loans={gapData?.loansWithoutServicingData}
        loading={gapLoading}
        error={Boolean(gapError)}
      />
    </main>
  );
};
